import {
  buildTenantSessionScaffoldHeaders,
  clinicianWorkbenchActionContractVersion,
  clinicianWorkbenchActionHttpRoutePath,
  clinicianWorkbenchDocumentCompleteUploadContractVersion,
  clinicianWorkbenchDocumentCompleteUploadHttpRoutePath,
  clinicianWorkbenchDocumentUploadIntentContractVersion,
  clinicianWorkbenchDocumentUploadIntentHttpRoutePath,
  clinicianWorkbenchOutboundCallsAnswersPdfDownloadHttpRoutePath,
  clinicianWorkbenchOutboundCallsCommandContractVersion,
  clinicianWorkbenchOutboundCallsCommandHttpRoutePath,
  clinicianWorkbenchOutboundCallsContractVersion,
  clinicianWorkbenchOutboundCallsHttpRoutePath,
  clinicianWorkbenchContractVersion,
  clinicianWorkbenchHttpRoutePath,
  clinicianWorkbenchQuestionnaireDefinitionContractVersion,
  clinicianWorkbenchQuestionnaireDefinitionHttpRoutePath,
  workforceSessionAccessTokenContractVersion,
  workforceSessionAccessTokenRoutePath,
  workforceSessionAuthorizeContractVersion,
  workforceSessionAuthorizeRoutePath,
  workforceSessionCurrentContractVersion,
  workforceSessionCurrentRoutePath,
  workforceSessionLoginContractVersion,
  workforceSessionLoginRoutePath,
  tenantSessionBootstrapHeaderNames,
  tenantSessionCodeChallengeMethod,
  tenantSessionPreviewBootstrapContractVersion,
  tenantSessionPreviewBootstrapRoutePath,
  type ClinicianWorkbenchActionResponseV1,
  type ClinicianWorkbenchDocumentCompleteUploadRequestV1,
  type ClinicianWorkbenchDocumentCompleteUploadResponseV1,
  type ClinicianWorkbenchDocumentUploadIntentResponseV1,
  type ClinicianWorkbenchHttpQuery,
  type ClinicianWorkbenchOutboundCallsCommandResponseV1,
  type ClinicianWorkbenchOutboundCallsRequestV1,
  type ClinicianWorkbenchOutboundCallsResponseV1,
  type ClinicianWorkbenchQuestionnaireDefinitionResponseV1,
  type ClinicianWorkbenchResponseV1,
  type TenantSessionPreviewBootstrapRequestV1,
  type TenantSessionPreviewBootstrapResponseV1,
  type WorkforceSessionAccessTokenResponseV1,
  type WorkforceSessionAuthorizeRequestV1,
  type WorkforceSessionAuthorizeResponseV1,
  type WorkforceSessionCurrentResponseV1,
  type WorkforceSessionLoginRequestV1,
  type WorkforceSessionLoginResponseV1,
  type WorkforceSessionSummaryV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";

import type {
  ClinicianWorkbenchActionTransportRequest,
  ClinicianWorkbenchDocumentUploadTransportRequest,
  ClinicianWorkbenchQuestionnaireDefinitionTransportRequest,
  ClinicianWorkbenchTransport,
  ClinicianWorkbenchTransportRequest,
} from "./workbench-client.js";
import { uploadPreparedDocumentBytes } from "./document-upload-client.js";

/** A refusal carried back from the API, with the parts the surface needs kept
 *  apart from the log line. The `message` keeps its original shape so the
 *  string-matching callers (stale-token detection, logs) are unaffected. */
export class WorkbenchFetchError extends Error {
  readonly status: number;
  readonly errorCode: string | null;
  readonly serverMessage: string | null;

  constructor(input: {
    message: string;
    status: number;
    errorCode: string | null;
    serverMessage: string | null;
  }) {
    super(input.message);
    this.name = "WorkbenchFetchError";
    this.status = input.status;
    this.errorCode = input.errorCode;
    this.serverMessage = input.serverMessage;
  }
}

export async function buildWorkbenchFetchError(
  response: Pick<Response, "json" | "status">,
  prefix: string,
): Promise<Error> {
  let errorCode: string | null = null;
  let message: string | null = null;

  try {
    const payload = (await response.json()) as unknown;

    if (typeof payload === "object" && payload !== null) {
      const candidate = payload as Record<string, unknown>;

      errorCode = typeof candidate.errorCode === "string" ? candidate.errorCode : null;
      message = typeof candidate.message === "string" ? candidate.message : null;
    }
  } catch {
    // Some infrastructure failures have no JSON body; the status still identifies them.
  }

  return new WorkbenchFetchError({
    errorCode,
    message: `${prefix}-${String(response.status)}${errorCode ? `-${errorCode}` : ""}${
      message ? `: ${message}` : ""
    }`,
    serverMessage: message,
    status: response.status,
  });
}

/** A stale token is the one refusal the shell resolves by itself — it triggers
 *  a workspace refresh — so it gets copy that tells the clinician to save
 *  again rather than the server's internal phrasing. */
const staleMaterialChangeTokenFeedback =
  "This patient's record changed while you were editing. The workspace has been refreshed — please check your entries and save again.";

export function resolveWorkbenchActionErrorFeedback(error: unknown): string | null {
  if (!(error instanceof Error)) {
    return null;
  }

  if (
    error.message.includes("stale-material-change-token") ||
    error.message.includes("expected materialChangeToken")
  ) {
    return staleMaterialChangeTokenFeedback;
  }

  // Every 4xx the API returns carries a message written for the clinician —
  // "this questionnaire does not have a resumable session", "the selected
  // target does not have a callable phone number". Showing it beats the
  // generic "could not be saved", which sent a tester back to us with a
  // screenshot and no way to tell a stale page from a locked questionnaire.
  // 5xx bodies are not authored for anyone to read, so they keep the generic
  // copy.
  if (
    error instanceof WorkbenchFetchError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.serverMessage
  ) {
    return error.serverMessage;
  }

  const marker = "patient-record-field-value-invalid: ";
  const markerIndex = error.message.indexOf(marker);

  if (markerIndex < 0) {
    return null;
  }

  return error.message.slice(markerIndex + marker.length).trim() || null;
}

function resolveFetchBaseUrl(config: MobileRuntimeConfig): string {
  if (config.apiBaseUrl) {
    return config.apiBaseUrl;
  }

  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin;
  }

  throw new Error("clinician-workbench-fetch-base-url-unavailable");
}

function resolveSessionFetchBaseUrl(config: MobileRuntimeConfig): string {
  if (config.sessionApiBaseUrl) {
    return config.sessionApiBaseUrl;
  }

  return resolveFetchBaseUrl(config);
}

function appendQueryParam(
  searchParams: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void {
  if (value == null || value.length === 0) {
    return;
  }

  searchParams.set(key, value);
}

function buildFetchUrl(
  query: ClinicianWorkbenchHttpQuery,
  config: MobileRuntimeConfig,
): string {
  const url = new URL(clinicianWorkbenchHttpRoutePath, resolveFetchBaseUrl(config));
  const searchParams = new URLSearchParams();

  appendQueryParam(searchParams, "tenantId", query.tenantId);
  appendQueryParam(searchParams, "siteId", query.siteId ?? null);
  appendQueryParam(searchParams, "clinicianUserId", query.clinicianUserId);
  appendQueryParam(searchParams, "listScope", query.listScope ?? null);
  appendQueryParam(searchParams, "attentionOnly", query.attentionOnly ?? null);
  appendQueryParam(searchParams, "searchQuery", query.searchQuery ?? null);
  appendQueryParam(
    searchParams,
    "selectedPatientId",
    query.selectedPatientId ?? null,
  );
  appendQueryParam(searchParams, "detailTab", query.detailTab ?? null);
  url.search = searchParams.toString();

  return url.toString();
}

function buildActionFetchUrl(config: MobileRuntimeConfig): string {
  return new URL(
    clinicianWorkbenchActionHttpRoutePath,
    resolveFetchBaseUrl(config),
  ).toString();
}

function buildDocumentUploadIntentFetchUrl(config: MobileRuntimeConfig): string {
  return new URL(
    clinicianWorkbenchDocumentUploadIntentHttpRoutePath,
    resolveFetchBaseUrl(config),
  ).toString();
}

function buildDocumentCompleteUploadFetchUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    clinicianWorkbenchDocumentCompleteUploadHttpRoutePath,
    resolveFetchBaseUrl(config),
  ).toString();
}

function buildOutboundCallsFetchUrl(
  request: ClinicianWorkbenchOutboundCallsRequestV1,
  config: MobileRuntimeConfig,
): string {
  const url = new URL(
    clinicianWorkbenchOutboundCallsHttpRoutePath,
    resolveFetchBaseUrl(config),
  );
  const searchParams = new URLSearchParams();

  appendQueryParam(searchParams, "tenantId", request.tenantId);
  appendQueryParam(searchParams, "siteId", request.siteId);
  appendQueryParam(searchParams, "clinicianUserId", request.clinicianUserId);
  appendQueryParam(searchParams, "patientId", request.patientId);
  url.search = searchParams.toString();

  return url.toString();
}

function buildOutboundCallsCommandFetchUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    clinicianWorkbenchOutboundCallsCommandHttpRoutePath,
    resolveFetchBaseUrl(config),
  ).toString();
}

function buildQuestionnaireDefinitionFetchUrl(
  request: ClinicianWorkbenchQuestionnaireDefinitionTransportRequest["request"],
  config: MobileRuntimeConfig,
): string {
  const url = new URL(
    clinicianWorkbenchQuestionnaireDefinitionHttpRoutePath,
    resolveFetchBaseUrl(config),
  );
  const searchParams = new URLSearchParams();

  appendQueryParam(searchParams, "tenantId", request.tenantId);
  appendQueryParam(searchParams, "siteId", request.siteId);
  appendQueryParam(searchParams, "clinicianUserId", request.clinicianUserId);
  appendQueryParam(
    searchParams,
    "questionnaireTemplateVersionId",
    request.questionnaireTemplateVersionId ?? null,
  );
  url.search = searchParams.toString();

  return url.toString();
}

function buildOutboundCallsAnswersPdfDownloadFetchUrl(
  input: {
    patientId: string;
    sessionId?: string | null;
    targetId: string;
  },
  config: MobileRuntimeConfig,
): string {
  const url = new URL(
    clinicianWorkbenchOutboundCallsAnswersPdfDownloadHttpRoutePath,
    resolveFetchBaseUrl(config),
  );
  const searchParams = new URLSearchParams();

  appendQueryParam(searchParams, "tenantId", config.requestTenantId);
  appendQueryParam(searchParams, "siteId", config.requestSiteId);
  appendQueryParam(searchParams, "clinicianUserId", config.requestClinicianUserId);
  appendQueryParam(searchParams, "patientId", input.patientId);
  appendQueryParam(searchParams, "targetId", input.targetId);
  appendQueryParam(searchParams, "sessionId", input.sessionId ?? null);
  url.search = searchParams.toString();

  return url.toString();
}

function readContentDispositionFilename(value: string | null): string | null {
  const normalized = value?.trim() ?? "";

  if (!normalized) {
    return null;
  }

  const quotedMatch = /filename="([^"]+)"/iu.exec(normalized);

  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const tokenMatch = /filename=([^;]+)/iu.exec(normalized);

  return tokenMatch?.[1]?.trim() ?? null;
}

function buildPreviewSessionBootstrapUrl(config: MobileRuntimeConfig): string {
  return new URL(
    tenantSessionPreviewBootstrapRoutePath,
    resolveFetchBaseUrl(config),
  ).toString();
}

async function readWorkbenchFetchErrorMessage(
  response: Response,
): Promise<string | null> {
  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      const payload = (await response.json()) as { message?: unknown };
      const message =
        typeof payload.message === "string" ? payload.message.trim() : "";

      return message.length > 0 ? message : null;
    }

    const text = (await response.text()).trim();

    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

function buildWorkforceSessionAccessTokenUrl(config: MobileRuntimeConfig): string {
  return new URL(
    workforceSessionAccessTokenRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildWorkforceSessionLoginUrl(config: MobileRuntimeConfig): string {
  return new URL(
    workforceSessionLoginRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildWorkforceSessionCurrentUrl(config: MobileRuntimeConfig): string {
  return new URL(
    workforceSessionCurrentRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildWorkforceSessionAuthorizeUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    workforceSessionAuthorizeRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPreviewSessionBootstrapRequest(
  config: MobileRuntimeConfig,
): TenantSessionPreviewBootstrapRequestV1 {
  return {
    schemaVersion: tenantSessionPreviewBootstrapContractVersion,
    tenantId: config.requestTenantId,
    siteId: config.requestSiteId,
    clinicianUserId: config.requestClinicianUserId,
  };
}

function buildWorkforceSessionAuthorizeRequest(
  codeChallenge: string,
): WorkforceSessionAuthorizeRequestV1 {
  return {
    schemaVersion: workforceSessionAuthorizeContractVersion,
    codeChallenge,
    codeChallengeMethod: tenantSessionCodeChallengeMethod,
  };
}

function buildWorkforceSessionLoginRequest(
  config: MobileRuntimeConfig,
): WorkforceSessionLoginRequestV1 {
  return {
    schemaVersion: workforceSessionLoginContractVersion,
    tenantId: config.requestTenantId,
    ...(config.workforceSessionSubjectKey
      ? {
          subjectKey: config.workforceSessionSubjectKey,
        }
      : {}),
  };
}

function readWorkforceSessionSubjectKey(
  session: WorkforceSessionSummaryV1,
): string | null {
  if (typeof session.subjectKey === "string" && session.subjectKey.length > 0) {
    return session.subjectKey;
  }

  return null;
}

function matchesWorkforceSessionConfig(
  config: MobileRuntimeConfig,
  payload: WorkforceSessionCurrentResponseV1 | WorkforceSessionLoginResponseV1,
): boolean {
  const subjectKey = readWorkforceSessionSubjectKey(payload.session);

  return (
    payload.session.tenantId === config.requestTenantId &&
    (config.workforceSessionSubjectKey == null ||
      subjectKey === config.workforceSessionSubjectKey)
  );
}

function buildWorkforceSessionAccessTokenExchangeRequest(
  authorizationCode: string,
  codeVerifier: string,
) {
  return {
    schemaVersion: workforceSessionAccessTokenContractVersion,
    grantType: "authorization_code" as const,
    authorizationCode,
    codeVerifier,
  };
}

function supportsBrowserPkce(): boolean {
  return (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.getRandomValues === "function" &&
    typeof globalThis.crypto.subtle !== "undefined"
  );
}

function resolvePkceCrypto(): Crypto {
  if (
    typeof globalThis.crypto === "undefined" ||
    typeof globalThis.crypto.getRandomValues !== "function" ||
    typeof globalThis.crypto.subtle === "undefined"
  ) {
    throw new Error("clinician-workbench-pkce-crypto-unavailable");
  }

  return globalThis.crypto;
}

function encodeBase64UrlBytes(bytes: Uint8Array): string {
  const binary = Array.from(
    bytes,
    (value) => String.fromCharCode(value),
  ).join("");

  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function buildPkceCodeVerifier(): string {
  const randomBytes = new Uint8Array(32);

  resolvePkceCrypto().getRandomValues(randomBytes);

  return encodeBase64UrlBytes(randomBytes);
}

async function buildPkceCodeChallenge(
  codeVerifier: string,
): Promise<string> {
  const digest = await resolvePkceCrypto().subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier),
  );

  return encodeBase64UrlBytes(new Uint8Array(digest));
}

function buildFetchHeaders(
  config: MobileRuntimeConfig,
  accessToken: string | null = null,
): Record<string, string> {
  return {
    accept: "application/json",
    ...(config.tenantSessionBootstrapMode === "bearer-token"
      ? {
          [tenantSessionBootstrapHeaderNames.bootstrapMode]: "bearer-token",
        }
      : {}),
    ...(accessToken
      ? {
          authorization: `Bearer ${accessToken}`,
        }
      : {}),
    ...buildTenantSessionScaffoldHeaders({
      bootstrapMode: config.tenantSessionBootstrapMode,
      roleKeys: config.tenantSessionRoleKeys,
      patientAccessMode: config.tenantSessionPatientAccessMode,
      siteId: config.tenantSessionSiteId,
    }),
  };
}

function resolveFetchCredentialsMode(
  config: MobileRuntimeConfig,
): "include" | "omit" {
  return config.tenantSessionBootstrapMode === "cookie-session"
    ? "include"
    : "omit";
}

function usesCookieSession(config: MobileRuntimeConfig): boolean {
  return config.tenantSessionBootstrapMode === "cookie-session";
}

function usesBearerToken(config: MobileRuntimeConfig): boolean {
  return config.tenantSessionBootstrapMode === "bearer-token";
}

export function createFetchClinicianWorkbenchTransport(
  config: MobileRuntimeConfig,
): ClinicianWorkbenchTransport {
  let previewSessionBootstrapState: "idle" | "ready" = "idle";
  let previewSessionBootstrapPromise: Promise<void> | null = null;
  let workforceSessionState: "idle" | "ready" = "idle";
  let workforceSessionPromise: Promise<WorkforceSessionSummaryV1> | null = null;
  let workforceSession: WorkforceSessionSummaryV1 | null = null;
  let accessTokenState: "idle" | "ready" = "idle";
  let accessTokenPromise: Promise<string> | null = null;
  let accessToken: string | null = null;

  function resetPreviewSessionBootstrapState(): void {
    previewSessionBootstrapState = "idle";
    previewSessionBootstrapPromise = null;
  }

  function resetAccessTokenState(): void {
    accessTokenState = "idle";
    accessTokenPromise = null;
    accessToken = null;
  }

  function resetWorkforceSessionState(): void {
    workforceSessionState = "idle";
    workforceSessionPromise = null;
    workforceSession = null;
  }

  async function ensurePreviewCookieSession(): Promise<void> {
    if (!usesCookieSession(config) || previewSessionBootstrapState === "ready") {
      return;
    }

    if (previewSessionBootstrapPromise) {
      return previewSessionBootstrapPromise;
    }

    previewSessionBootstrapPromise = (async () => {
      const response = await fetch(buildPreviewSessionBootstrapUrl(config), {
        body: JSON.stringify(buildPreviewSessionBootstrapRequest(config)),
        credentials: resolveFetchCredentialsMode(config),
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-session-bootstrap-failed-${response.status}`,
        );
      }

      const payload =
        (await response.json()) as TenantSessionPreviewBootstrapResponseV1;

      if (
        payload.schemaVersion !== tenantSessionPreviewBootstrapContractVersion ||
        payload.bootstrapMode !== "cookie-session"
      ) {
        throw new Error("clinician-workbench-session-bootstrap-schema-mismatch");
      }

      previewSessionBootstrapState = "ready";
    })();

    try {
      await previewSessionBootstrapPromise;
    } catch (error) {
      resetPreviewSessionBootstrapState();
      throw error;
    }
  }

  async function ensureWorkforceSession(): Promise<WorkforceSessionSummaryV1> {
    if (!usesBearerToken(config)) {
      throw new Error("clinician-workbench-workforce-session-mode-disabled");
    }

    if (workforceSessionState === "ready" && workforceSession) {
      return workforceSession;
    }

    if (workforceSessionPromise) {
      return workforceSessionPromise;
    }

    workforceSessionPromise = (async () => {
      const currentResponse = await fetch(buildWorkforceSessionCurrentUrl(config), {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
        method: "GET",
      });

      if (currentResponse.ok) {
        const currentPayload =
          (await currentResponse.json()) as WorkforceSessionCurrentResponseV1;

        if (
          currentPayload.schemaVersion === workforceSessionCurrentContractVersion &&
          currentPayload.sessionMode === "browser-session" &&
          readWorkforceSessionSubjectKey(currentPayload.session) != null &&
          matchesWorkforceSessionConfig(config, currentPayload)
        ) {
          workforceSession = currentPayload.session;
          workforceSessionState = "ready";
          return workforceSession;
        }
      } else if (currentResponse.status !== 401) {
        throw new Error(
          `clinician-workbench-workforce-session-current-failed-${currentResponse.status}`,
        );
      }

      const response = await fetch(buildWorkforceSessionLoginUrl(config), {
        body: JSON.stringify(buildWorkforceSessionLoginRequest(config)),
        credentials: "include",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-workforce-session-login-failed-${response.status}`,
        );
      }

      const payload =
        (await response.json()) as WorkforceSessionLoginResponseV1;

      if (
        payload.schemaVersion !== workforceSessionLoginContractVersion ||
        payload.sessionMode !== "browser-session" ||
        readWorkforceSessionSubjectKey(payload.session) == null ||
        !matchesWorkforceSessionConfig(config, payload)
      ) {
        throw new Error("clinician-workbench-workforce-session-login-schema-mismatch");
      }

      workforceSession = payload.session;
      workforceSessionState = "ready";
      return workforceSession;
    })();

    try {
      return await workforceSessionPromise;
    } catch (error) {
      resetWorkforceSessionState();
      throw error;
    }
  }

  async function ensureAccessToken(): Promise<string> {
    if (!usesBearerToken(config)) {
      throw new Error("clinician-workbench-access-token-mode-disabled");
    }

    if (accessTokenState === "ready" && accessToken) {
      return accessToken;
    }

    if (accessTokenPromise) {
      return accessTokenPromise;
    }

    accessTokenPromise = (async () => {
      await ensureWorkforceSession();

      if (!supportsBrowserPkce()) {
        throw new Error("clinician-workbench-pkce-crypto-unavailable");
      }

      const codeVerifier = buildPkceCodeVerifier();
      const codeChallenge = await buildPkceCodeChallenge(codeVerifier);
      const authorizeResponse = await fetch(
        buildWorkforceSessionAuthorizeUrl(config),
        {
          body: JSON.stringify(
            buildWorkforceSessionAuthorizeRequest(codeChallenge),
          ),
          credentials: "include",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          method: "POST",
        },
      );

      if (!authorizeResponse.ok) {
        if (authorizeResponse.status === 401) {
          resetWorkforceSessionState();
        }

        throw await buildWorkbenchFetchError(
          authorizeResponse,
          "clinician-workbench-workforce-session-authorize-failed",
        );
      }

      const authorizePayload =
        (await authorizeResponse.json()) as WorkforceSessionAuthorizeResponseV1;

      if (
        authorizePayload.schemaVersion !==
          workforceSessionAuthorizeContractVersion ||
        authorizePayload.bootstrapMode !== "bearer-token" ||
        authorizePayload.grantType !== "authorization_code" ||
        authorizePayload.codeChallengeMethod !==
          tenantSessionCodeChallengeMethod ||
        typeof authorizePayload.authorizationCode !== "string" ||
        authorizePayload.authorizationCode.length === 0
      ) {
        throw new Error(
          "clinician-workbench-workforce-session-authorize-schema-mismatch",
        );
      }

      const response = await fetch(buildWorkforceSessionAccessTokenUrl(config), {
        body: JSON.stringify(
          buildWorkforceSessionAccessTokenExchangeRequest(
            authorizePayload.authorizationCode,
            codeVerifier,
          ),
        ),
        credentials: "omit",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw await buildWorkbenchFetchError(
          response,
          "clinician-workbench-access-token-bootstrap-failed",
        );
      }

      const payload =
        (await response.json()) as WorkforceSessionAccessTokenResponseV1;

      if (
        payload.schemaVersion !== workforceSessionAccessTokenContractVersion ||
        payload.bootstrapMode !== "bearer-token" ||
        payload.tokenType !== "Bearer" ||
        typeof payload.accessToken !== "string" ||
        payload.accessToken.length === 0
      ) {
        throw new Error("clinician-workbench-access-token-schema-mismatch");
      }

      accessToken = payload.accessToken;
      accessTokenState = "ready";

      return payload.accessToken;
    })();

    try {
      return await accessTokenPromise;
    } catch (error) {
      resetAccessTokenState();
      throw error;
    }
  }

  async function fetchWithSessionBootstrapRetry(
    requestFactory: () => Promise<Response>,
  ): Promise<Response> {
    if (usesCookieSession(config)) {
      await ensurePreviewCookieSession();

      let response = await requestFactory();

      if (response.status !== 401) {
        return response;
      }

      resetPreviewSessionBootstrapState();
      await ensurePreviewCookieSession();
      response = await requestFactory();

      return response;
    }

    if (usesBearerToken(config)) {
      await ensureAccessToken();

      let response = await requestFactory();

      if (response.status !== 401) {
        return response;
      }

      resetAccessTokenState();
      resetWorkforceSessionState();
      await ensureAccessToken();
      response = await requestFactory();

      return response;
    }

    return requestFactory();
  }

  return {
    async readClinicianWorkbench(
      input: ClinicianWorkbenchTransportRequest,
    ): Promise<ClinicianWorkbenchResponseV1> {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildFetchUrl(input.query, config), {
          credentials: resolveFetchCredentialsMode(config),
          headers: buildFetchHeaders(config, accessToken),
          method: "GET",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        throw new Error(`clinician-workbench-fetch-failed-${response.status}`);
      }

      const payload =
        (await response.json()) as ClinicianWorkbenchResponseV1;

      if (payload.schemaVersion !== clinicianWorkbenchContractVersion) {
        throw new Error("clinician-workbench-schema-version-mismatch");
      }

      return payload;
    },

    async submitClinicianWorkbenchAction(
      input: ClinicianWorkbenchActionTransportRequest,
    ): Promise<ClinicianWorkbenchActionResponseV1> {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildActionFetchUrl(config), {
          body: JSON.stringify(input.request),
          credentials: resolveFetchCredentialsMode(config),
          headers: {
            ...buildFetchHeaders(config, accessToken),
            "content-type": "application/json",
          },
          method: "POST",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        throw await buildWorkbenchFetchError(
          response,
          "clinician-workbench-action-fetch-failed",
        );
      }

      const payload =
        (await response.json()) as ClinicianWorkbenchActionResponseV1;

      if (payload.schemaVersion !== clinicianWorkbenchActionContractVersion) {
        throw new Error("clinician-workbench-action-schema-version-mismatch");
      }

      return payload;
    },

    async uploadClinicianWorkbenchDocument(
      input: ClinicianWorkbenchDocumentUploadTransportRequest,
    ): Promise<ClinicianWorkbenchDocumentCompleteUploadResponseV1> {
      const intentResponse = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildDocumentUploadIntentFetchUrl(config), {
          body: JSON.stringify({
            schemaVersion:
              clinicianWorkbenchDocumentUploadIntentContractVersion,
            ...input.request,
          }),
          credentials: resolveFetchCredentialsMode(config),
          headers: {
            ...buildFetchHeaders(config, accessToken),
            "content-type": "application/json",
          },
          method: "POST",
          signal: input.signal,
        }),
      );

      if (!intentResponse.ok) {
        throw new Error(
          `clinician-workbench-document-upload-intent-failed-${intentResponse.status}`,
        );
      }

      const intentPayload =
        (await intentResponse.json()) as ClinicianWorkbenchDocumentUploadIntentResponseV1;

      if (
        intentPayload.schemaVersion !==
          clinicianWorkbenchDocumentUploadIntentContractVersion ||
        intentPayload.uploadMethod !== "PUT"
      ) {
        throw new Error(
          "clinician-workbench-document-upload-intent-schema-mismatch",
        );
      }

      if (input.request.byteLength > intentPayload.maxByteLength) {
        throw new Error("clinician-workbench-document-upload-too-large");
      }

      await uploadPreparedDocumentBytes(
        {
          bytes: input.bytes,
          uploadHeaders: intentPayload.uploadHeaders,
          uploadUrl: intentPayload.uploadUrl,
        },
        input.signal,
      );

      const completeUploadRequest: ClinicianWorkbenchDocumentCompleteUploadRequestV1 =
        {
          schemaVersion:
            clinicianWorkbenchDocumentCompleteUploadContractVersion,
          workbenchRequest: input.request.workbenchRequest,
          patientId: input.request.patientId,
          uploadId: intentPayload.uploadId,
          documentId: intentPayload.documentId,
          byteLength: input.request.byteLength,
          sha256: input.request.sha256,
          title: input.request.title,
          documentTypeKey: input.request.documentTypeKey,
        };
      const completeResponse = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildDocumentCompleteUploadFetchUrl(config), {
          body: JSON.stringify(completeUploadRequest),
          credentials: resolveFetchCredentialsMode(config),
          headers: {
            ...buildFetchHeaders(config, accessToken),
            "content-type": "application/json",
          },
          method: "POST",
          signal: input.signal,
        }),
      );

      if (!completeResponse.ok) {
        throw new Error(
          `clinician-workbench-document-complete-upload-failed-${completeResponse.status}`,
        );
      }

      const completePayload =
        (await completeResponse.json()) as ClinicianWorkbenchDocumentCompleteUploadResponseV1;

      if (
        completePayload.schemaVersion !==
          clinicianWorkbenchDocumentCompleteUploadContractVersion
      ) {
        throw new Error(
          "clinician-workbench-document-complete-upload-schema-mismatch",
        );
      }

      return completePayload;
    },

    async readClinicianWorkbenchOutboundCalls(
      input,
    ): Promise<ClinicianWorkbenchOutboundCallsResponseV1> {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildOutboundCallsFetchUrl(input.request, config), {
          credentials: resolveFetchCredentialsMode(config),
          headers: buildFetchHeaders(config, accessToken),
          method: "GET",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        const errorMessage = await readWorkbenchFetchErrorMessage(response);

        throw new Error(
          [
            `clinician-workbench-outbound-calls-fetch-failed-${response.status}`,
            errorMessage,
          ]
            .filter(Boolean)
            .join(": "),
        );
      }

      const payload =
        (await response.json()) as ClinicianWorkbenchOutboundCallsResponseV1;

      if (payload.schemaVersion !== clinicianWorkbenchOutboundCallsContractVersion) {
        throw new Error(
          "clinician-workbench-outbound-calls-schema-version-mismatch",
        );
      }

      return payload;
    },

    async readClinicianWorkbenchQuestionnaireDefinition(
      input,
    ): Promise<ClinicianWorkbenchQuestionnaireDefinitionResponseV1> {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildQuestionnaireDefinitionFetchUrl(input.request, config), {
          credentials: resolveFetchCredentialsMode(config),
          headers: buildFetchHeaders(config, accessToken),
          method: "GET",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        const errorMessage = await readWorkbenchFetchErrorMessage(response);

        throw new Error(
          [
            `clinician-workbench-questionnaire-definition-fetch-failed-${response.status}`,
            errorMessage,
          ]
            .filter(Boolean)
            .join(": "),
        );
      }

      const payload =
        (await response.json()) as ClinicianWorkbenchQuestionnaireDefinitionResponseV1;

      if (
        payload.schemaVersion !==
        clinicianWorkbenchQuestionnaireDefinitionContractVersion
      ) {
        throw new Error(
          "clinician-workbench-questionnaire-definition-schema-version-mismatch",
        );
      }

      return payload;
    },

    async downloadClinicianWorkbenchOutboundQuestionnaireAnswersPdf(
      input,
    ) {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildOutboundCallsAnswersPdfDownloadFetchUrl(input, config), {
          credentials: resolveFetchCredentialsMode(config),
          headers: buildFetchHeaders(config, accessToken),
          method: "GET",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        const errorMessage = await readWorkbenchFetchErrorMessage(response);

        throw new Error(
          [
            `clinician-workbench-outbound-calls-answers-pdf-fetch-failed-${response.status}`,
            errorMessage,
          ]
            .filter(Boolean)
            .join(": "),
        );
      }

      return {
        bodyBytes: await response.arrayBuffer(),
        contentType: response.headers.get("content-type") ?? "application/pdf",
        filename:
          readContentDispositionFilename(
            response.headers.get("content-disposition"),
          ) ?? "vip-by-amplecare-preop-answers.pdf",
      };
    },

    async submitClinicianWorkbenchOutboundCallsCommand(
      input,
    ): Promise<ClinicianWorkbenchOutboundCallsCommandResponseV1> {
      const response = await fetchWithSessionBootstrapRetry(() =>
        fetch(buildOutboundCallsCommandFetchUrl(config), {
          body: JSON.stringify(input.request),
          credentials: resolveFetchCredentialsMode(config),
          headers: {
            ...buildFetchHeaders(config, accessToken),
            "content-type": "application/json",
          },
          method: "POST",
          signal: input.signal,
        }),
      );

      if (!response.ok) {
        const errorMessage = await readWorkbenchFetchErrorMessage(response);

        throw new Error(
          [
            `clinician-workbench-outbound-calls-command-fetch-failed-${response.status}`,
            errorMessage,
          ]
            .filter(Boolean)
            .join(": "),
        );
      }

      const payload =
        (await response.json()) as ClinicianWorkbenchOutboundCallsCommandResponseV1;

      if (
        payload.schemaVersion !==
        clinicianWorkbenchOutboundCallsCommandContractVersion
      ) {
        throw new Error(
          "clinician-workbench-outbound-calls-command-schema-version-mismatch",
        );
      }

      return payload;
    },
  };
}
